import {
  captureEvent,
  captureException,
  getClient,
  setUser,
  type Event as SentryEvent,
  type User as SentryUser,
} from '@sentry/nextjs';

import { MonitoringService } from '@kit/monitoring-core';

/**
 * @class
 * @implements {MonitoringService}
 * ServerSentryMonitoringService is responsible for capturing exceptions and identifying users using the Sentry monitoring service.
 */
export class SentryMonitoringService implements MonitoringService {
  private readonly readyPromise: Promise<unknown>;
  private readyResolver?: (value?: unknown) => void;

  constructor() {
    this.readyPromise = new Promise(
      (resolve) => (this.readyResolver = resolve),
    );

    void this.initialize();
  }

  async ready() {
    return this.readyPromise;
  }

  captureException(error: Error | null) {
    return captureException(error);
  }

  captureEvent<Extra extends SentryEvent>(event: string, extra?: Extra) {
    return captureEvent({
      message: event,
      ...extra,
    });
  }

  identifyUser(user: SentryUser) {
    setUser(user);
  }

  private async initialize() {
    const environment =
      process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ?? process.env.VERCEL_ENV;

    if (typeof document !== 'undefined') {
      // A host app's client instrumentation hook (Next.js
      // `instrumentation-client.ts`) runs BEFORE any client component module,
      // and it is the only place that can register browser-only integrations
      // such as replayIntegration. If it already initialized a client, calling
      // init() again here replaces that client with this bare one — silently
      // dropping its integrations, sample rates and drop telemetry. Yield to it.
      if (getClient()) {
        this.readyResolver?.();
        return;
      }

      const { initializeSentryBrowserClient } =
        await import('../sentry.client.config');

      initializeSentryBrowserClient({
        environment,
      });
    } else {
      const { initializeSentryServerClient } =
        await import('../sentry.server.config');

      initializeSentryServerClient({
        environment,
      });
    }

    this.readyResolver?.();
  }
}
